import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

type Mode = "live" | "demo";

// Toggle this to show/hide demo mode badges across all pages
export const SHOW_DEMO_MODE_BADGES = false;

// List of all screens that have demo/live mode toggle
export const SCREENS_WITH_MODE_TOGGLE = [
  "stock-items",
  "create-stock-item",
  "custom-fields",
  "stock-item-detail",
  "warehouses-locations",
  "stock-movements",
  "dashboard",
  "stock-takes",
  "warehouse-location-setup",
] as const;

export type ScreenId = (typeof SCREENS_WITH_MODE_TOGGLE)[number];

interface ScreenModes {
  [screenId: string]: Mode;
}

interface ModeContextType {
  // Legacy API for backward compatibility (uses current screen or default)
  mode: Mode;
  setMode: (mode: Mode) => void;
  isDemo: boolean;
  isLive: boolean;

  // New screen-specific API
  getScreenMode: (screenId: ScreenId) => Mode;
  setScreenMode: (screenId: ScreenId, mode: Mode) => void;
  isScreenDemo: (screenId: ScreenId) => boolean;
  isScreenLive: (screenId: ScreenId) => boolean;

  // Global mode API
  areAllScreensLive: boolean;
  areAllScreensDemo: boolean;
  setAllScreensToLive: () => void;
  setAllScreensToDemo: () => void;
  screenModes: ScreenModes;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

const STORAGE_KEY = "app-screen-modes";

function loadScreenModes(): ScreenModes {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate and ensure all screens have a mode
      const modes: ScreenModes = {};
      for (const screenId of SCREENS_WITH_MODE_TOGGLE) {
        modes[screenId] = parsed[screenId] === "live" || parsed[screenId] === "demo" ? parsed[screenId] : "demo"; // Default to demo for new screens
      }
      return modes;
    }
  } catch {
    // Ignore parse errors
  }

  // Default all screens to demo mode
  const defaultModes: ScreenModes = {};
  for (const screenId of SCREENS_WITH_MODE_TOGGLE) {
    defaultModes[screenId] = "demo";
  }
  return defaultModes;
}

export function ModeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [screenModes, setScreenModes] = useState<ScreenModes>(loadScreenModes);

  // Persist to localStorage whenever screenModes changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(screenModes));
  }, [screenModes]);

  // Get mode for a specific screen
  const getScreenMode = useCallback(
    (screenId: ScreenId): Mode => {
      return screenModes[screenId] || "demo";
    },
    [screenModes],
  );

  // Set mode for a specific screen
  const setScreenMode = useCallback((screenId: ScreenId, mode: Mode) => {
    setScreenModes((prev) => ({
      ...prev,
      [screenId]: mode,
    }));
  }, []);

  // Check if a specific screen is in demo mode
  const isScreenDemo = useCallback(
    (screenId: ScreenId): boolean => {
      return getScreenMode(screenId) === "demo";
    },
    [getScreenMode],
  );

  // Check if a specific screen is in live mode
  const isScreenLive = useCallback(
    (screenId: ScreenId): boolean => {
      return getScreenMode(screenId) === "live";
    },
    [getScreenMode],
  );

  // Check if all screens are in live mode
  const areAllScreensLive = useMemo(() => {
    return SCREENS_WITH_MODE_TOGGLE.every((screenId) => screenModes[screenId] === "live");
  }, [screenModes]);

  // Check if all screens are in demo mode
  const areAllScreensDemo = useMemo(() => {
    return SCREENS_WITH_MODE_TOGGLE.every((screenId) => screenModes[screenId] === "demo");
  }, [screenModes]);

  // Set all screens to live mode
  const setAllScreensToLive = useCallback(() => {
    const newModes: ScreenModes = {};
    for (const screenId of SCREENS_WITH_MODE_TOGGLE) {
      newModes[screenId] = "live";
    }
    setScreenModes(newModes);
  }, []);

  // Set all screens to demo mode
  const setAllScreensToDemo = useCallback(() => {
    const newModes: ScreenModes = {};
    for (const screenId of SCREENS_WITH_MODE_TOGGLE) {
      newModes[screenId] = "demo";
    }
    setScreenModes(newModes);
  }, []);

  // Legacy API - uses 'stock-items' as default screen for backward compatibility
  const defaultScreenId: ScreenId = "stock-items";
  const mode = getScreenMode(defaultScreenId);
  const setMode = useCallback(
    (newMode: Mode) => {
      setScreenMode(defaultScreenId, newMode);
    },
    [setScreenMode],
  );

  const value: ModeContextType = {
    // Legacy API
    mode,
    setMode,
    isDemo: mode === "demo",
    isLive: mode === "live",

    // New screen-specific API
    getScreenMode,
    setScreenMode,
    isScreenDemo,
    isScreenLive,

    // Global mode API
    areAllScreensLive,
    areAllScreensDemo,
    setAllScreensToLive,
    setAllScreensToDemo,
    screenModes,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextType {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error("useMode must be used within a ModeProvider");
  }
  return context;
}

// Hook for screen-specific mode - use this in pages
export function useScreenMode(screenId: ScreenId) {
  const context = useMode();

  const mode = context.getScreenMode(screenId);
  const setMode = useCallback(
    (newMode: Mode) => {
      context.setScreenMode(screenId, newMode);
    },
    [context, screenId],
  );

  return {
    mode,
    setMode,
    isDemo: mode === "demo",
    isLive: mode === "live",
  };
}
